// Azure Retail Prices API base URL (served via our own Azure Function proxy)
export const PRICING_API_BASE = '/api/GetPrices';

// Fallback: only used in local development if the Azure Function isn't running
export const PRICING_API_FALLBACK = 'https://prices.azure.com/api/retail/prices';

/**
 * Builds the URL to fetch prices. Pass either `filterQuery` (for a fresh request)
 * or `nextPageUrl` (for pagination) – not both.
 *
 * - When `nextPageUrl` is provided the path and query string are extracted from it
 *   and re-routed through the local Azure Function proxy, so the browser always
 *   talks to `/api/GetPrices` rather than directly to prices.azure.com.
 * - When `filterQuery` is provided a normal first-page request is constructed.
 */
export const buildPricingUrl = (filterQuery, nextPageUrl = null) => {
  if (nextPageUrl) {
    // Strip the original host so we always route through our function
    const parsed = new URL(nextPageUrl);
    return `${PRICING_API_BASE}${parsed.search}`;
  }
  return `${PRICING_API_BASE}?${filterQuery}`;
};

// ---------------------------------------------------------------------------
// Azure Regions – comprehensive list of GA regions
// ---------------------------------------------------------------------------
export const regions = [
  // Americas
  { value: 'eastus', label: 'East US' },
  { value: 'eastus2', label: 'East US 2' },
  { value: 'westus', label: 'West US' },
  { value: 'westus2', label: 'West US 2' },
  { value: 'westus3', label: 'West US 3' },
  { value: 'centralus', label: 'Central US' },
  { value: 'northcentralus', label: 'North Central US' },
  { value: 'southcentralus', label: 'South Central US' },
  { value: 'westcentralus', label: 'West Central US' },
  { value: 'canadacentral', label: 'Canada Central' },
  { value: 'canadaeast', label: 'Canada East' },
  { value: 'brazilsouth', label: 'Brazil South' },
  { value: 'brazilsoutheast', label: 'Brazil Southeast' },
  // Europe
  { value: 'northeurope', label: 'North Europe' },
  { value: 'westeurope', label: 'West Europe' },
  { value: 'uksouth', label: 'UK South' },
  { value: 'ukwest', label: 'UK West' },
  { value: 'francecentral', label: 'France Central' },
  { value: 'francesouth', label: 'France South' },
  { value: 'germanywestcentral', label: 'Germany West Central' },
  { value: 'germanynorth', label: 'Germany North' },
  { value: 'switzerlandnorth', label: 'Switzerland North' },
  { value: 'switzerlandwest', label: 'Switzerland West' },
  { value: 'norwayeast', label: 'Norway East' },
  { value: 'norwaywest', label: 'Norway West' },
  { value: 'swedencentral', label: 'Sweden Central' },
  { value: 'polandcentral', label: 'Poland Central' },
  { value: 'italynorth', label: 'Italy North' },
  { value: 'spaincentral', label: 'Spain Central' },
  // Asia Pacific
  { value: 'eastasia', label: 'East Asia' },
  { value: 'southeastasia', label: 'Southeast Asia' },
  { value: 'japaneast', label: 'Japan East' },
  { value: 'japanwest', label: 'Japan West' },
  { value: 'australiaeast', label: 'Australia East' },
  { value: 'australiasoutheast', label: 'Australia Southeast' },
  { value: 'australiacentral', label: 'Australia Central' },
  { value: 'centralindia', label: 'Central India' },
  { value: 'southindia', label: 'South India' },
  { value: 'westindia', label: 'West India' },
  { value: 'koreacentral', label: 'Korea Central' },
  { value: 'koreasouth', label: 'Korea South' },
  { value: 'chinaeast2', label: 'China East 2' },
  { value: 'chinanorth2', label: 'China North 2' },
  // Middle East & Africa
  { value: 'uaenorth', label: 'UAE North' },
  { value: 'uaecentral', label: 'UAE Central' },
  { value: 'southafricanorth', label: 'South Africa North' },
  { value: 'southafricawest', label: 'South Africa West' },
  { value: 'israelcentral', label: 'Israel Central' },
  { value: 'qatarcentral', label: 'Qatar Central' },
  { value: 'mexicocentral', label: 'Mexico Central' },
  { value: 'newzealandnorth', label: 'New Zealand North' },
];

// ---------------------------------------------------------------------------
// Managed Disk types
// ---------------------------------------------------------------------------
export const diskTypes = [
  { value: 'Premium SSD', label: 'Premium SSD' },
  { value: 'Standard SSD', label: 'Standard SSD' },
  { value: 'Standard HDD', label: 'Standard HDD' },
  { value: 'Ultra Disk', label: 'Ultra Disk' },
];

// ---------------------------------------------------------------------------
// Compute price types
// ---------------------------------------------------------------------------
export const computePriceTypes = [
  { value: 'Consumption', label: 'Pay-as-you-go' },
  { value: 'Reserved', label: 'Reserved' },
];

const computeExclusionsFilter =
  "serviceFamily eq 'Compute' and contains(productName, 'Cloud Services') ne true and contains(skuName, 'Spot') ne true and contains(skuName, 'Low Priority') ne true";

export const escapeODataString = (value = '') => value.trim().replace(/'/g, "''");

export const buildComputePricingFilter = ({ region, sku, priceType }) => {
  const escapedRegion = escapeODataString(region);
  const escapedSku = escapeODataString(sku);
  const escapedPriceType = escapeODataString(priceType);

  return `armRegionName eq '${escapedRegion}' and armSkuName eq '${escapedSku}' and priceType eq '${escapedPriceType}' and ${computeExclusionsFilter}`;
};

export const buildComputeSkuSuggestionFilter = (searchTerm) => {
  const escapedSearchTerm = escapeODataString(searchTerm);
  return `contains(armSkuName, '${escapedSearchTerm}') and ${computeExclusionsFilter}`;
};

export const getComputeSkuSuggestions = (items = [], searchTerm = '', limit = 8) => {
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const skuNames = [...new Set(
    items
      .map((item) => item.armSkuName || item.skuName)
      .filter(Boolean)
  )];

  return skuNames
    .filter((skuName) => (
      !normalizedSearchTerm || skuName.toLowerCase().includes(normalizedSearchTerm)
    ))
    .sort((left, right) => {
      const normalizedLeft = left.toLowerCase();
      const normalizedRight = right.toLowerCase();
      const leftIsExact = normalizedLeft === normalizedSearchTerm;
      const rightIsExact = normalizedRight === normalizedSearchTerm;

      if (leftIsExact !== rightIsExact) {
        return leftIsExact ? -1 : 1;
      }

      const leftStartsWith = normalizedLeft.startsWith(normalizedSearchTerm);
      const rightStartsWith = normalizedRight.startsWith(normalizedSearchTerm);

      if (leftStartsWith !== rightStartsWith) {
        return leftStartsWith ? -1 : 1;
      }

      return left.localeCompare(right);
    })
    .slice(0, limit);
};

// ---------------------------------------------------------------------------
// Format a unit price as currency with unit of measure
// ---------------------------------------------------------------------------
export const formatPrice = (price, currency = 'USD', unitOfMeasure = '') => {
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 4,
    maximumFractionDigits: 6,
  }).format(price);
  return unitOfMeasure ? `${formatted} / ${unitOfMeasure}` : formatted;
};
